import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: ${theme.colors.softGray};
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease-in-out;
  opacity: ${props => props.loaded ? 1 : 0};
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.softGray};
  color: ${theme.colors.slateGray};
  font-size: 24px;
`;

const ErrorPlaceholder = styled(Placeholder)`
  background-color: #f5f5f5;
  color: #999;
  font-size: 20px;
  flex-direction: column;
  gap: 8px;
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #aaa;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid ${theme.colors.softGray};
  border-top-color: ${theme.colors.brandYellow};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const LazyImage = ({ src, alt, placeholder = '📷' }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const handleLoad = () => {
    setLoaded(true);
    setError(false);
  };

  const handleError = () => {
    setError(true);
    setLoaded(false);
  };

  return (
    <ImageContainer ref={imgRef}>
      {!loaded && !error && isVisible && <LoadingSpinner />}
      {!loaded && !error && !isVisible && <Placeholder>{placeholder}</Placeholder>}
      {error && (
        <ErrorPlaceholder>
          <span>🖼️</span>
          <ErrorText>Image unavailable</ErrorText>
        </ErrorPlaceholder>
      )}
      {isVisible && !error && (
        <StyledImage
          src={src}
          alt={alt}
          loaded={loaded}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </ImageContainer>
  );
};

export default LazyImage;
